import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

/**
 * MembershipManager - Full CRUD for UserHealthSystemMembership
 * Surfaces ALL fields from the model with inline editing similar to EditableUserDetails
 */
const MembershipManager = ({ userId, onUpdate }) => {
  const [memberships, setMemberships] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    health_system_id: '',
    role_id: '',
    external_identifiers: {},
    identifier_source: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membershipsRes, healthSystemsRes, rolesRes] = await Promise.all([
        adminApi.getUserMemberships(userId),
        adminApi.getHealthSystems(),
        adminApi.getRoles({ category: 'healthcare_user' })
      ]);

      setMemberships(membershipsRes.data?.memberships || []);
      setHealthSystems(healthSystemsRes.data?.health_systems || []);
      setRoles(rolesRes.data?.roles || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load memberships');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingId(null);
    setFormData({
      health_system_id: '',
      role_id: '',
      external_identifiers: {},
      identifier_source: '',
      is_active: true
    });
  };

  const handleEdit = (membership) => {
    setEditingId(membership.id);
    setShowAddForm(true);
    setFormData({
      health_system_id: membership.health_system?.id || '',
      role_id: membership.role?.id || '',
      external_identifiers: membership.external_identifiers || {},
      identifier_source: membership.identifier_source || '',
      is_active: membership.is_active
    });
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await adminApi.updateUserMembership(userId, editingId, formData);
      } else {
        await adminApi.createUserMembership(userId, formData);
      }
      setShowAddForm(false);
      setEditingId(null);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save membership');
    }
  };

  const handleDelete = async (membershipId) => {
    if (!window.confirm('Are you sure you want to delete this membership?')) return;

    try {
      await adminApi.deleteUserMembership(userId, membershipId);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete membership');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
  };

  const updateExternalIdentifier = (key, value) => {
    setFormData(prev => ({
      ...prev,
      external_identifiers: { ...prev.external_identifiers, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-4">
          <div className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header py-2 bg-light d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="fas fa-hospital me-2" />
          Health System Memberships
        </h6>
        {!showAddForm && (
          <button className="btn btn-sm btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-1" />Add Membership
          </button>
        )}
      </div>

      <div className="card-body p-2">
        {error && <div className="alert alert-danger small mb-2">{error}</div>}

        {showAddForm && (
          <div className="border rounded p-3 mb-3 bg-light">
            <h6 className="mb-3">{editingId ? 'Edit Membership' : 'Add Membership'}</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label small mb-1">Health System *</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.health_system_id}
                  onChange={e => setFormData({ ...formData, health_system_id: e.target.value })}
                  required
                >
                  <option value="">Select Health System</option>
                  {healthSystems.map(hs => (
                    <option key={hs.id} value={hs.id}>{hs.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label small mb-1">Role *</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.role_id}
                  onChange={e => setFormData({ ...formData, role_id: e.target.value })}
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label small mb-1">Identifier Source</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formData.identifier_source}
                  onChange={e => setFormData({ ...formData, identifier_source: e.target.value })}
                  placeholder="e.g., Epic, Athena, Mirth"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label small mb-1">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={formData.is_active}
                  onChange={e => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label small mb-1">External Identifiers (JSON)</label>
                <div className="border rounded p-2 bg-white">
                  <small className="text-muted d-block mb-2">
                    Common fields: mrn, ehr_patient_id, ehr_provider_id, mirth_id
                  </small>
                  {['mrn', 'ehr_patient_id', 'ehr_provider_id', 'mirth_id'].map(key => (
                    <div key={key} className="input-group input-group-sm mb-1">
                      <span className="input-group-text">{key}</span>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.external_identifiers[key] || ''}
                        onChange={e => updateExternalIdentifier(key, e.target.value)}
                        placeholder={`Enter ${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="btn-group btn-group-sm">
              <button className="btn btn-success" onClick={handleSave}>
                <i className="fas fa-check me-1" />
                {editingId ? 'Update' : 'Create'}
              </button>
              <button className="btn btn-secondary" onClick={handleCancel}>
                <i className="fas fa-times me-1" />Cancel
              </button>
            </div>
          </div>
        )}

        {memberships.length === 0 ? (
          <div className="text-center text-muted small py-3">
            <i className="fas fa-hospital me-1" />
            No memberships configured
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Health System</th>
                  <th>Role</th>
                  <th>Identifier Source</th>
                  <th>External IDs</th>
                  <th>Status</th>
                  <th>Granted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {memberships.map(m => (
                  <tr key={m.id}>
                    <td>
                      <strong>{m.health_system?.name || '-'}</strong>
                      <div className="small text-muted"><b>Core ID: </b>{m.health_system?.health_system_id}</div>
                      <div className="small text-muted"><b>UUID: </b>{m.health_system?.id}</div>
                    </td>
                    <td>{m.role?.name || '-'}</td>
                    <td>{m.identifier_source || '-'}</td>
                    <td>
                      {Object.keys(m.external_identifiers || {}).length > 0 ? (
                        <div className="small">
                          {Object.entries(m.external_identifiers).map(([key, val]) => (
                            <div key={key}><strong>{key}:</strong> {val}</div>
                          ))}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`badge bg-${m.is_active ? 'success' : 'secondary'}`}>
                        {m.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="small">
                      {m.granted_at && new Date(m.granted_at).toLocaleDateString()}
                      {m.granted_by && (
                        <div className="text-muted">
                          by {m.granted_by.first_name} {m.granted_by.last_name}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleEdit(m)}
                          title="Edit"
                        >
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(m.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembershipManager;
